import React, { useMemo } from 'react';
import { Box, Flex, Circle, Text } from '@tapresearch/ui/components';
import { SliceTooltipProps } from '@nivo/line';

export const LineChartSliceTooltip = ({
  slice,
  axis,
  visiblePoints,
  waveNumber,
  usePercentage,
}: React.PropsWithChildren<
  SliceTooltipProps & {
    visiblePoints: number[];
    waveNumber: number;
    usePercentage: boolean;
  }
>) => {
  const otherAxis = axis === 'x' ? 'y' : 'x';

  const orderedPoints = useMemo(() => {
    return slice.points
      .slice()
      .reverse()
      .filter((_, i) =>
        !visiblePoints.length ? true : visiblePoints.includes(i)
      )
      .sort((a, b) => Number(b.data.yFormatted) - Number(a.data.yFormatted));
  }, [visiblePoints, slice.points]);

  const sliceDate = orderedPoints[0].data.x;

  return (
    <Box
      rounded="md"
      border="1px solid"
      borderColor="gray.100"
      boxShadow="2xl"
      bg="white"
      overflow="hidden"
      p={2}
      minW="250px"
    >
      <Flex
        justify="space-between"
        align="center"
        py={3}
        px={1}
        mx={2}
        mb={2}
        color="gray.700"
        fontSize="md"
        fontWeight="medium"
        borderBottom="1px solid"
        borderBottomColor="gray.100"
      >
        Wave #{waveNumber}
        <Flex grow={1} fontWeight="normal" color="gray.300" justify="flex-end">
          {sliceDate}
        </Flex>
      </Flex>
      <Box pb={2} px={4} my={2}>
        {orderedPoints.map((point, index) => {
          const pointData =
            point.data[`${otherAxis}Formatted` as 'xFormatted' | 'yFormatted'];

          return (
            <Flex
              key={`row-${index}`}
              justifyContent="space-between"
              alignItems="center"
              my={2}
              py={1}
              _last={{
                mb: 0,
              }}
            >
              <Circle
                as={Flex}
                alignItems="center"
                bgColor={point.serieColor}
                h={3}
                w={3}
              />
              <Text
                as="span"
                fontSize="sm"
                ml={2}
                textAlign="left"
                flexGrow={1}
              >
                {point.serieId}
              </Text>
              <Text as="span" fontSize="sm" ml={3} fontWeight="bold">
                {parseFloat(pointData as string)
                  ? `${+parseFloat(pointData as string).toFixed(2)}${
                      usePercentage ? '%' : ''
                    }`
                  : pointData}
              </Text>
            </Flex>
          );
        })}
      </Box>
    </Box>
  );
};
